const AUTH_KEY = "klrn_admin_auth";
const ACTIVITY_KEY = "klrn_admin_activity";
const ATTEMPTS_KEY = "klrn_admin_attempts";
const LOCKOUT_KEY = "klrn_admin_lockout";
const PASSCODE_KEY = "klrn_admin_passcode";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours
const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 15 * 60 * 1000; // 15 minutes

// ===== Session management =====

function isAdminAuthenticated() {
  const auth = sessionStorage.getItem(AUTH_KEY);
  if (!auth) return false;

  const lastActivity = sessionStorage.getItem(ACTIVITY_KEY);
  if (lastActivity) {
    const elapsed = Date.now() - parseInt(lastActivity, 10);
    if (elapsed > SESSION_TIMEOUT_MS) {
      clearAdminSession();
      return false;
    }
  }

  // Refresh activity on check
  sessionStorage.setItem(ACTIVITY_KEY, Date.now().toString());
  return true;
}

function setAdminAuthenticated(passcode) {
  sessionStorage.setItem(AUTH_KEY, "true");
  sessionStorage.setItem(ACTIVITY_KEY, Date.now().toString());
  // Store the passcode for API calls (session only, not persisted)
  if (passcode) {
    sessionStorage.setItem(PASSCODE_KEY, passcode);
  }
  // Reset attempts on successful login
  localStorage.removeItem(ATTEMPTS_KEY);
  localStorage.removeItem(LOCKOUT_KEY);
}

function clearAdminSession() {
  sessionStorage.removeItem(AUTH_KEY);
  sessionStorage.removeItem(ACTIVITY_KEY);
  sessionStorage.removeItem(PASSCODE_KEY);
}

// Get the stored passcode for API calls (only available after authentication)
function getAdminPasscode() {
  return sessionStorage.getItem(PASSCODE_KEY) || "";
}

// ─── Owner-JWT passcode bootstrap (Feb 2026 fix) ─────────────────────
// Karine reported that "Add Note / Add Points / Redeem Points always says
// Invalid passcode, but sometimes on the 4th or 5th click it works". Root
// cause: Head Office login authenticates via an owner JWT cookie and does
// NOT populate the legacy `klrn_admin_passcode` in sessionStorage. Some
// widgets (CustomPlansPanel, AvailabilityAuditWidget) auto-fetch it on
// their own useEffect; when they land first, sessionStorage gets seeded
// and subsequent clicks succeed — that's why retrying "eventually works".
//
// The fix below runs ONCE per tab, as early as this module is imported,
// so every admin action sees the passcode immediately. It's a no-op if:
//   • the passcode is already in sessionStorage
//   • the user has no owner JWT (regular passcode login flow)
let _passcodeBootPromise = null;
function ensureAdminPasscode(force = false) {
  // `force=true` explicitly re-fetches even if sessionStorage already has
  // a value — used to recover from stale-cache 403s (Karine's Feb 2026
  // report: after updating ADMIN_PASSCODE in Deployment Secrets and
  // redeploying, her browser's sessionStorage still had the OLD value
  // so every admin action returned "Invalid passcode" until she manually
  // cleared cache).
  if (!force && sessionStorage.getItem(PASSCODE_KEY)) return Promise.resolve(true);
  if (_passcodeBootPromise) return _passcodeBootPromise;
  _passcodeBootPromise = (async () => {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      let r;
      try {
        r = await fetch(`${BACKEND_URL}/api/admin/passcode-for-owner`, {
          credentials: "include",
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timer);
      }
      if (!r.ok) {
        _passcodeBootPromise = null;
        return false;
      }
      const d = await r.json();
      if (d && d.passcode) {
        sessionStorage.setItem(PASSCODE_KEY, d.passcode);
        _passcodeBootPromise = null;
        return true;
      }
    } catch {
      // silent — the caller will see 403 if they truly aren't authorized
    }
    _passcodeBootPromise = null;
    return false;
  })();
  return _passcodeBootPromise;
}

// Auto-boot: as soon as this module is loaded, kick off a passcode fetch
// so the very first user click already has it in sessionStorage.
if (typeof window !== "undefined") {
  ensureAdminPasscode();
}

// ─── Shared adminFetch helper (Feb 2026) ────────────────────────────
// Every admin endpoint that requires the passcode should go through this
// wrapper. It:
//   1. Bootstraps the passcode from JWT if sessionStorage is empty
//   2. Merges the passcode into the request body (JSON) or query string
//   3. If the backend returns 403 "Invalid passcode", force-refreshes the
//      passcode and retries ONCE — recovers from stale cache silently
//   4. Returns the parsed JSON response
//
// Usage:
//   const data = await adminFetch(`${API}/api/admin/foo`, { method: "POST", body: {...} });
async function adminFetch(url, opts = {}) {
  await ensureAdminPasscode();
  const doOnce = async () => {
    const passcode = getAdminPasscode();
    const method = (opts.method || "GET").toUpperCase();
    let finalUrl = url;
    const headers = { ...(opts.headers || {}) };
    let body = opts.body;
    if (method === "GET" || method === "DELETE") {
      // Append passcode as query param if not already present
      const sep = finalUrl.includes("?") ? "&" : "?";
      finalUrl = `${finalUrl}${sep}passcode=${encodeURIComponent(passcode)}`;
    } else {
      // POST/PUT/PATCH — merge into JSON body
      headers["Content-Type"] = headers["Content-Type"] || "application/json";
      if (typeof body === "object" && body !== null && !(body instanceof FormData)) {
        body = JSON.stringify({ ...body, passcode });
      } else if (typeof body === "string") {
        try {
          const parsed = JSON.parse(body);
          body = JSON.stringify({ ...parsed, passcode });
        } catch {
          // leave body untouched for non-JSON payloads
        }
      } else if (body == null) {
        body = JSON.stringify({ passcode });
      }
    }
    const resp = await fetch(finalUrl, { ...opts, method, headers, body, credentials: "include" });
    let parsed = null;
    try {
      parsed = await resp.clone().json();
    } catch {
      parsed = null;
    }
    return { resp, parsed };
  };

  let { resp, parsed } = await doOnce();
  const staleCache =
    resp.status === 403 &&
    parsed &&
    typeof parsed.detail === "string" &&
    /invalid\s*passcode/i.test(parsed.detail);
  if (staleCache) {
    sessionStorage.removeItem(PASSCODE_KEY);
    const fresh = await ensureAdminPasscode(true);
    if (fresh) {
      ({ resp, parsed } = await doOnce());
    }
  }
  return { resp, data: parsed };
}

// ===== Brute-force protection =====

function getLockoutState() {
  const lockoutUntil = localStorage.getItem(LOCKOUT_KEY);
  if (lockoutUntil) {
    const remaining = parseInt(lockoutUntil, 10) - Date.now();
    if (remaining > 0) {
      return { locked: true, remainingMs: remaining };
    }
    // Lockout expired — reset
    localStorage.removeItem(LOCKOUT_KEY);
    localStorage.removeItem(ATTEMPTS_KEY);
  }
  return { locked: false, remainingMs: 0 };
}

function getFailedAttempts() {
  return parseInt(localStorage.getItem(ATTEMPTS_KEY) || "0", 10);
}

function recordFailedAttempt() {
  const count = getFailedAttempts() + 1;
  localStorage.setItem(ATTEMPTS_KEY, count.toString());

  if (count >= MAX_ATTEMPTS) {
    const lockUntil = Date.now() + LOCKOUT_MS;
    localStorage.setItem(LOCKOUT_KEY, lockUntil.toString());
    return { locked: true, attempts: count };
  }
  return { locked: false, attempts: count };
}

// Progressive delay: 0s, 1s, 2s, 3s, 5s before attempt
function getAttemptDelay() {
  const attempts = getFailedAttempts();
  const delays = [0, 1000, 2000, 3000, 5000];
  return delays[Math.min(attempts, delays.length - 1)];
}

// Server-side passcode verification (secure - passcode validated on server)
async function verifyPasscode(code) {
  try {
    const response = await fetch(`${BACKEND_URL}/api/admin/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ passcode: code })
    });
    
    if (!response.ok) {
      return false;
    }
    
    const data = await response.json();
    if (data.valid) {
      setAdminAuthenticated(code);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Admin verification error:', error);
    return false;
  }
}

// PASSCODE getter - returns the stored passcode after successful authentication
// This is used by admin pages for API calls that require passcode authorization
function PASSCODE() {
  return getAdminPasscode();
}

export {
  PASSCODE,
  MAX_ATTEMPTS,
  isAdminAuthenticated,
  setAdminAuthenticated,
  clearAdminSession,
  verifyPasscode,
  getLockoutState,
  getFailedAttempts,
  recordFailedAttempt,
  getAttemptDelay,
  getAdminPasscode,
  ensureAdminPasscode,
  adminFetch,
  SESSION_TIMEOUT_MS,
};
